import _ from "lodash";
import {
  Component,
  createRef,
} from "react";
import {
  withLocation,
} from "CommonCore/router-util";
import {
  Button,
  ButtonToolbar,
  Dropdown,
  DropdownButton,
  Table,
} from "react-bootstrap";
import cls from "classnames";
import numeral from "numeral";

import store from "CommonState/store";
import ActionsDropdown from "CommonComponents/actions-dropdown";
import ColumnSelector from "CommonComponents/column-selector";
import FixedColumnSelector from "CommonComponents/fixed-column-selector";
import FilterBar from "CommonComponents/filter-bar";
import {
  Awesome,
  CustomDateField,
  formatValue,
  SchemaHeader,
  Spinner,
} from "CommonComponents/bricks";
import {
  getInnerText,
  isReactElement,
} from "CommonCore/util";
import exportTable from "CommonCore/export-table";
import {
  injectFunction,
} from "CommonCore/schema-util";

class HeaderWithSort extends Component {
  render() {
    const { field, sortable, sort, fit } = this.props;
    const sortCond = _.find(sort, s => s.key === field);
    return <th
      onClick={sortable ? this.onClick : undefined} className={cls({
        sortable: sortable && !sortCond,
        "sort-asc": sortCond && !sortCond.reverse,
        "sort-desc": sortCond && sortCond.reverse,
        "td-fit": fit,
      })}>
      <SchemaHeader useHeader {...this.props} />
      {sortable && <span>
        <Awesome name='sort' className='no-sort' />
        <Awesome name='sort-down' className='desc' />
        <Awesome name='sort-up' className='asc' />
      </span>}
    </th>;
  }

  onClick = evt => {
    const { field, onClick } = this.props;
    onClick(field, evt.ctrlKey);
  };
}

const ItemsPerPageChoices = [10, 25, 50, 100];

const DefaultTypeAlign = {
  money: "right",
  number: "right",
  checkbox: "center",
};

const ACTIONS_FIXED_FIELD = "__actions__";

const CurrentTimeReferenceTemplate = _.template("Stand: <%= date %>");

@withLocation
@store("currentTimeReference")
export default class EntityTable extends Component {
  constructor() {
    super();

    this.state = {
      canScrollLeft: false,
      canScrollRight: false,
    };
    this.mainTablePaneRef = createRef();
    this.fixedColumnsTablePaneRef = createRef();
    this.actionsTablePaneRef = createRef();
    this.mainTheadRef = createRef();
    this.fixedColumnsTheadRef = createRef();
    this.actionsTheadRef = createRef();
    this.tableWrapperRef = createRef();
    this.bottomScrollbarRef = createRef();
    this.bottomScrollbarContentRef = createRef();
    this.syncHeightsRaf = null;
    this.stickyRaf = null;
    this.pendingScrollLeft = null;
    this.scrollTargetResetTimer = null;
  }

  componentDidMount() {
    this.scheduleTableLayoutSync();
    this.updateScrollButtons();
    window.addEventListener("resize", this.onWindowResize);
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
  }

  componentDidUpdate() {
    this.scheduleTableLayoutSync();
    this.updateScrollButtons();
    this.onWindowScroll();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
    window.removeEventListener("scroll", this.onWindowScroll);
    if (this.stickyRaf) {
      window.cancelAnimationFrame(this.stickyRaf);
      this.stickyRaf = null;
    }
    if (this.syncHeightsRaf) {
      window.cancelAnimationFrame(this.syncHeightsRaf);
      this.syncHeightsRaf = null;
    }
    if (this.scrollTargetResetTimer) {
      window.clearTimeout(this.scrollTargetResetTimer);
      this.scrollTargetResetTimer = null;
    }
  }

  getVisibleSelectedItems() {
    const { data } = this.props;
    if (!data) {
      return [];
    }
    const { items, selection } = data;
    if (!items || !selection) {
      return [];
    }

    const lookup = _.keyBy(items, this.toKey);
    return _.map(selection, (selected, key) => selected ? key : void 0)
      .map(selectedKey => lookup[selectedKey]).filter(_.identity);
  }

  get tableSchema() {
    const {
      schema,
      data: { columnConfig = {} } = {},
      injects,
    } = this.props;
    return schema.table.filter(t => (!_.isObject(t.hideFromList) ? !t.hideFromList : !injectFunction(t.hideFromList, injects)())
      && ((!columnConfig.hasOwnProperty(t.field) && !t.initiallyHidden)
        || columnConfig[t.field]));
  }

  toKey = item => {
    const { toKey, schema } = this.props;
    if (schema.id) {
      return _.isFunction(schema.id) ? schema.id(item) : item[schema.id];
    }
    return toKey(item);
  };

  render() {
    const {
      list,
      data,
      schema,
      header,
      addNewPath,
      actions,
      actionsStyle,
      injects,
      renderMethods = {},
      currentTimeReference,
      toolbarComponents,
      toolbarComponentsLeft,
      multiItemActions,
      multiItemActionsDefault,
      multiItemStartTitle,
      multiItemDropDownTitle,
      multiItemDisabledInfo,
      filterProps,
      noStickyHeader,
      noHover,
      noStriped,
      overrideInflight,
    } = this.props;

    const {
      activeMultiItemAction,
    } = this.state;

    const {
      items = [],
      sort,
      itemsPerPage = 10,
      page = 0,
      reportedItemCount,
      columnConfig = {},
      fixedColumnConfig = {},
      inflight,
      selection,
    } = data;

    const canExport = true;

    const itemsCount = reportedItemCount || items.length;
    const totalPages = Math.ceil(itemsCount / itemsPerPage);
    const firstItemIndex = itemsPerPage * page;
    const shownItemCount = Math.min(firstItemIndex + itemsPerPage, itemsCount) - firstItemIndex;

    const itemsToRender = reportedItemCount ? items : items.slice(firstItemIndex, firstItemIndex + shownItemCount);

    const paginatorStartPage = Math.max(0, page - 6);
    const paginatorEndPage = Math.min(page + 7, totalPages);

    const tableSchema = this.tableSchema;
    const maxFixedColumns = tableSchema.length + (actions ? 1 : 0);
    const configuredFixedColumnSet = new Set(
      Object.keys(fixedColumnConfig).filter(key => fixedColumnConfig[key]),
    );
    const actionsFixed = !!actions && configuredFixedColumnSet.has(ACTIONS_FIXED_FIELD);
    const fixedColumns = tableSchema
      .filter(column => configuredFixedColumnSet.has(column.field))
      .slice(0, maxFixedColumns - (actionsFixed ? 1 : 0));
    const fixedColumnSet = new Set(fixedColumns.map(column => column.field));
    const scrollableColumns = tableSchema.filter(column => !fixedColumnSet.has(column.field));

    const visibleSelectedItems = this.getVisibleSelectedItems();
    const activeMultiItemActionIndex = _.isFinite(parseInt(activeMultiItemAction, 10))
      ? parseInt(activeMultiItemAction, 10)
      : null;
    const MultiItemActionComponent = activeMultiItemActionIndex !== null
      && multiItemActions
      && multiItemActions[activeMultiItemActionIndex]
      && multiItemActions[activeMultiItemActionIndex].component;

    const addText = schema.displayName ? `${ schema.displayName } hinzufügen` : "Hinzufügen";
    const multiItemDisabledText = _.isFunction(multiItemDisabledInfo) ? multiItemDisabledInfo(visibleSelectedItems) : null;

    return <div className='entity-list'>
      <div>
        {header}
        {schema.requiresTimeReference &&
          <CustomDateField
            timestamp={currentTimeReference}
            labelClassName='time-reference-datefield'
            onChange={this.onTimeReferenceChange}
            template={CurrentTimeReferenceTemplate} />}
        {toolbarComponentsLeft && <div className='toolbar'>
          {toolbarComponentsLeft}
        </div>}
        <ButtonToolbar className='align-right'>
          {toolbarComponents}
          {multiItemActions && (!selection
            ? <Button variant='outline-primary' onClick={this.enableSelection}>{multiItemStartTitle || "Mehrfach Aktionen"}</Button>
            : multiItemActions.length > 1
              ? <DropdownButton
                title={(multiItemDropDownTitle || multiItemStartTitle || "Mehrfach Aktionen") + (multiItemDisabledText ? ` (${ multiItemDisabledText })` : ` (${ _.isEmpty(visibleSelectedItems) ? "0" : visibleSelectedItems.length } selektiert )`)}
                variant='outline-primary'
                className='float-right'
                id={`${ list }-Mehrfach-Aktionen`}
                onSelect={this.multiItemActionSelected}>
                {multiItemActions.map((action, i) => <Dropdown.Item
                  key={i}
                  eventKey={i.toString()}
                  disabled={visibleSelectedItems.length === 0 || (_.isFunction(action.checkDisabled) ? action.checkDisabled(visibleSelectedItems) : false)}>
                  {action.display}
                </Dropdown.Item>)}
                <Dropdown.Divider />
                <Dropdown.Item eventKey='cancel'>Beenden</Dropdown.Item>
              </DropdownButton>
              : <span><Button
                data-message={0}
                variant='outline-primary'
                disabled={visibleSelectedItems.length === 0 || (_.isFunction(multiItemActions[0].checkDisabled) ? multiItemActions[0].checkDisabled(visibleSelectedItems) : false)}
                onClick={this.multiItemActionSelected}>
                {multiItemActions[0].display.replace("<selected>", visibleSelectedItems.length)}{multiItemDisabledText ? ` (${ multiItemDisabledText })` : ""}
              </Button><Button variant='outline-primary' onClick={this.disableSelection}>Auswahl beenden</Button>{"\u00a0"}</span>
          )}
          {canExport &&
            <DropdownButton
              title='Exportieren'
              id={`${ list }-export`}
              variant='outline-primary'
              className='float-right'
              onSelect={this.exportData}
              disabled={itemsCount === 0}>
              <Dropdown.Item eventKey='csv'>Exportieren als CSV</Dropdown.Item>
              <Dropdown.Item eventKey='excel'>Exportieren als Excel</Dropdown.Item>
            </DropdownButton>
          }
          {addNewPath && <Button variant='primary' onClick={this.add}><Awesome name='plus' />&nbsp;{addText}</Button>}
          <ColumnSelector
            list={list}
            schema={schema}
            columnConfig={columnConfig}
            injects={injects} />
          <FixedColumnSelector
            list={list}
            schema={schema}
            fixedColumnConfig={fixedColumnConfig}
            injects={injects}
            hasActions={!!actions}
            maxFixedColumns={maxFixedColumns} />
        </ButtonToolbar>
      </div>
      <div className='filter-wrap'>
        <FilterBar
          list={list}
          schema={schema}
          injects={injects}
          {...data}
          {...filterProps} />
      </div>
      <div
        className={cls("table-wrapper", { "table-wrapper-with-actions": actionsFixed })}
        ref={this.tableWrapperRef}
        tabIndex={0}
        onKeyDownCapture={this.onTableKeyDown}>
        <div
          className='table-bottom-scrollbar'
          ref={this.bottomScrollbarRef}
          onScroll={this.onBottomScrollbarScroll}>
          <div
            className='table-bottom-scrollbar-content'
            ref={this.bottomScrollbarContentRef} />
        </div>
        <div className='table-split-layout'>
          {fixedColumns.length > 0 && <div className='table-fixed-container table-fixed-container-left' ref={this.fixedColumnsTablePaneRef}>
            <Table bordered hover={!noHover} striped={!noStriped}>
              <thead className={noStickyHeader ? "" : "position-sticky"} ref={this.fixedColumnsTheadRef}>
                <tr>
                  {fixedColumns.map(c => <HeaderWithSort
                    {...c}
                    key={c.field}
                    sort={sort}
                    onClick={c.sortable ? this.setSortKey : undefined} />)}
                </tr>
              </thead>
              <tbody>
                {itemsToRender.map(d => {
                  const key = this.toKey(d);
                  return (
                    <tr
                      key={`fixed-${ key }`}
                      data-row-key={key}
                      onMouseEnter={this.onRowMouseEnter}
                      onMouseLeave={this.onRowMouseLeave}>
                      {fixedColumns.map(schemaItem => {
                        const align = schemaItem.align || DefaultTypeAlign[schemaItem.type];
                        return <td
                          key={schemaItem.field}
                          className={cls({
                            "td-fit": schemaItem.fit,
                            oneline: schemaItem.oneline,
                            [`align-${ align }`]: align,
                          })}
                          style={schemaItem.customstyle}>
                          {formatValue(d, schemaItem, renderMethods, injects, this.props)}
                        </td>;
                      })}
                    </tr>);
                })}
              </tbody>
            </Table>
          </div>}
          <div className='table-scroll-container' ref={this.mainTablePaneRef} onScroll={this.onScrollableTableScroll}>
            <Table bordered hover={!noHover} striped={!noStriped} className='table-compact-layout'>
              <thead className={noStickyHeader ? "" : "position-sticky"} ref={this.mainTheadRef}>
                <tr>
                  {multiItemActions && selection && <th className='td-fit' onClick={this.selectAllItems}>
                    <input
                      type='checkbox'
                      readOnly
                      checked={itemsCount === visibleSelectedItems.length}
                      onClick={this.selectAllItemsCheckbox}
                    />
                  </th>}
                  {multiItemActionsDefault && <th className='td-fit' onClick={this.selectAllItems}>
                    <input
                      type='checkbox'
                      readOnly
                      checked={!!itemsCount && itemsCount === visibleSelectedItems.length}
                      onClick={this.selectAllItemsCheckbox}
                    />
                  </th>}
                  {scrollableColumns.map(c => <HeaderWithSort
                    {...c}
                    key={c.field}
                    sort={sort}
                    onClick={c.sortable ? this.setSortKey : undefined} />)}
                  {!!actions && !actionsFixed && <th className='td-fit'>Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {itemsToRender.map(d => {
                  const key = this.toKey(d);
                  return (
                    <tr
                      key={key}
                      data-row-key={key}
                      onMouseEnter={this.onRowMouseEnter}
                      onMouseLeave={this.onRowMouseLeave}>
                      {multiItemActions && selection && <td className='td-fit' onClick={this.onItemSelected} data-id={key}>
                        <input
                          type='checkbox'
                          readOnly
                          checked={selection[key] || false}
                        />
                      </td>}
                      {multiItemActionsDefault && <td className='td-fit' onClick={this.onItemSelected} data-id={key}>
                        <input
                          type='checkbox'
                          readOnly
                          checked={selection && selection[key] || false}
                        />
                      </td>}
                      {scrollableColumns.map(schemaItem => {
                        const align = schemaItem.align || DefaultTypeAlign[schemaItem.type];
                        return <td
                          key={schemaItem.field}
                          className={cls({
                            "td-fit": schemaItem.fit,
                            oneline: schemaItem.oneline,
                            [`align-${ align }`]: align,
                          })}
                          style={schemaItem.customstyle}>
                          {formatValue(d, schemaItem, renderMethods, injects, this.props)}
                        </td>;
                      })}
                      {!!actions && !actionsFixed && <td className='td-fit actions-cell'>
                        <ActionsDropdown
                          style={actionsStyle}
                          id={`actions-${ list }-${ key }`}
                          actions={actions}
                          item={d} />
                      </td>}
                    </tr>);
                })}
              </tbody>
            </Table>
          </div>
          {!!actions && actionsFixed && <div className='table-fixed-container table-fixed-container-right' ref={this.actionsTablePaneRef}>
            <Table bordered hover={!noHover} striped={!noStriped}>
              <thead className={noStickyHeader ? "" : "position-sticky"} ref={this.actionsTheadRef}>
                <tr>
                  <th className='td-fit'>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {itemsToRender.map(d => {
                  const key = this.toKey(d);
                  return (
                    <tr
                      key={`actions-${ key }`}
                      data-row-key={key}
                      onMouseEnter={this.onRowMouseEnter}
                      onMouseLeave={this.onRowMouseLeave}>
                      <td className='td-fit actions-cell'>
                        <ActionsDropdown
                          style={actionsStyle}
                          id={`actions-${ list }-${ key }`}
                          actions={actions}
                          item={d} />
                      </td>
                    </tr>);
                })}
              </tbody>
            </Table>
          </div>}
        </div>
        {(inflight || this.props.inflight || overrideInflight) && <div className='inflight-overlay'>
          <Spinner />
        </div>}
        {(!(inflight || this.props.inflight || overrideInflight) && itemsCount === 0) && <div className='table-empty-state'>
          Es sind keine Einträge vorhanden.
        </div>}
      </div>
      <div className='footer-wrap'>
        <span className='item-count'>{`${ Math.min(firstItemIndex + 1, itemsCount) } - ${ firstItemIndex + shownItemCount } von ${ itemsCount }`}</span>
        <span className='items-per-page'>pro Seite: {ItemsPerPageChoices.map(count => <Button key={count} variant='link' className={`${ itemsPerPage === count ? "selected" : "" }`} onClick={() => this.setItemsPerPage(count)}>{count}</Button>)
        }</span>
        <span className='page-navigator'>
          <Button
            variant='link'
            disabled={page === 0}
            onClick={() => this.setPage(0)}>
            <Awesome name='angle-double-left' />
          </Button>
          <Button
            variant='link'
            disabled={page === 0}
            onClick={() => this.setPage(page - 1)}>
            <Awesome name='angle-left' />
          </Button>
          {paginatorStartPage > 0 && "..."}
          {_.range(paginatorStartPage, paginatorEndPage).map(p => <Button key={p} variant='link' onClick={() => this.setPage(p)} className={`${ page === p ? "selected" : "" }`}>{p + 1}</Button>)}
          {paginatorEndPage < totalPages && "..."}
          <Button
            variant='link'
            disabled={page === totalPages - 1}
            onClick={() => this.setPage(page + 1)}>
            <Awesome name='angle-right' />
          </Button>
          <Button
            variant='link'
            disabled={page === totalPages - 1}
            onClick={() => this.setPage(totalPages - 1)}>
            <Awesome name='angle-double-right' />
          </Button>
        </span>
      </div>
      {MultiItemActionComponent && <MultiItemActionComponent
        items={visibleSelectedItems}
        action={multiItemActions[activeMultiItemActionIndex]}
        onComplete={this.onMultiItemActionComplete}
        onCancel={this.onMultiItemActionCancel}
      />}
    </div>;
  }

  add = () => {
    const { addNewPath, history } = this.props;
    history.push(addNewPath);
  };

  setSortKey = (key, cascade) => {
    const { setUiListSortKey, list } = this.props;
    setUiListSortKey(list, key, cascade);
  };

  setItemsPerPage = count => {
    const { setUiListItemsPerPage, list } = this.props;
    setUiListItemsPerPage(list, count);
  };

  setPage = page => {
    const { setUiListPage, list } = this.props;
    setUiListPage(list, page);
  };

  disableSelection = async() => {
    const { setUiListSelectionEnabled, list, afterMultiItemMode } = this.props;
    if (afterMultiItemMode) {
      await afterMultiItemMode(list);
    }
    setUiListSelectionEnabled(list, false);
    this.setState({ activeMultiItemAction: undefined });
  };

  enableSelection = async() => {
    const {
      setUiListSelectionEnabled,
      list,
      beforeMultiItemMode,
    } = this.props;
    if (beforeMultiItemMode) {
      await beforeMultiItemMode(list);
    }
    setUiListSelectionEnabled(list, true);
    if (this.props.onSelectionChanged) {
      await this.props.onSelectionChanged(this.getVisibleSelectedItems());
    }
  };

  onItemSelected = async({ currentTarget }) => {
    const id = currentTarget.getAttribute("data-id");
    if (!id) {
      return;
    }

    const { toggleUiListItemSelection, list } = this.props;
    await toggleUiListItemSelection(list, id);
    if (this.props.onSelectionChanged) {
      await this.props.onSelectionChanged(this.getVisibleSelectedItems());
    }
  };

  selectAllItems = async() => {
    const { toggleUiListItemSelection, list, data } = this.props;
    const items = data && data.items;
    if (items) {
      await toggleUiListItemSelection(list, items.map(this.toKey));
    }
    if (this.props.onSelectionChanged) {
      await this.props.onSelectionChanged(this.getVisibleSelectedItems());
    }
  };

  selectAllItemsCheckbox = async event => {
    event.stopPropagation();
    await this.selectAllItems();
  };

  multiItemActionSelected = async eventKey => {
    if (eventKey === "cancel") {
      const { setUiListSelectionEnabled, list, afterMultiItemMode } = this.props;
      if (afterMultiItemMode) {
        await afterMultiItemMode(list);
      }
      setUiListSelectionEnabled(list, false);
      this.setState({ activeMultiItemAction: undefined });
      return;
    }

    let selectedKey = eventKey;
    if (eventKey && eventKey.target) {
      selectedKey = parseInt(eventKey.target.getAttribute("data-message"), 10);
    } else {
      selectedKey = parseInt(eventKey, 10);
    }
    this.setState({ activeMultiItemAction: selectedKey });
  };

  onMultiItemActionComplete = () => {
    const { activeMultiItemAction } = this.state;
    this.setState({ activeMultiItemAction: undefined });
    this.props.afterMultiItemAction && this.props.afterMultiItemAction(activeMultiItemAction, false);
  };

  onMultiItemActionCancel = () => {
    const { activeMultiItemAction } = this.state;
    this.setState({ activeMultiItemAction: undefined });
    this.props.afterMultiItemAction && this.props.afterMultiItemAction(activeMultiItemAction, true);
  };

  onTimeReferenceChange = timeReference => {
    this.props.setCurrentTimeReference(timeReference);
  };

  onWindowResize = () => {
    this.scheduleTableLayoutSync();
    this.updateScrollButtons();
  };

  onWindowScroll = () => {
    if (this.stickyRaf) {
      return;
    }

    this.stickyRaf = window.requestAnimationFrame(() => {
      this.stickyRaf = null;
      const wrapper = this.tableWrapperRef.current;
      if (!wrapper) {
        return;
      }

      this.syncBottomScrollbarPosition();

      const { noStickyHeader } = this.props;
      if (noStickyHeader) {
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const headerHeight = Math.max(
        this.mainTheadRef.current ? this.mainTheadRef.current.getBoundingClientRect().height : 0,
        this.fixedColumnsTheadRef.current ? this.fixedColumnsTheadRef.current.getBoundingClientRect().height : 0,
        this.actionsTheadRef.current ? this.actionsTheadRef.current.getBoundingClientRect().height : 0,
      );
      const maxOffset = Math.max(0, wrapperRect.height - headerHeight);
      const offset = wrapperRect.top < 0
        ? Math.min(-wrapperRect.top, maxOffset)
        : 0;
      [this.mainTheadRef.current, this.fixedColumnsTheadRef.current, this.actionsTheadRef.current].forEach(thead => {
        if (thead) {
          thead.style.transform = offset > 0 ? `translateY(${ offset }px)` : "";
        }
      });
    });
  };

  onScrollableTableScroll = () => {
    const scrollPane = this.mainTablePaneRef.current;
    const bottomScrollbar = this.bottomScrollbarRef.current;
    if (scrollPane && bottomScrollbar && Math.abs(bottomScrollbar.scrollLeft - scrollPane.scrollLeft) > 1) {
      bottomScrollbar.scrollLeft = scrollPane.scrollLeft;
    }
    if (scrollPane && this.pendingScrollLeft !== null && Math.abs(scrollPane.scrollLeft - this.pendingScrollLeft) < 1) {
      this.pendingScrollLeft = null;
    }
    if (this.scrollTargetResetTimer) {
      window.clearTimeout(this.scrollTargetResetTimer);
    }
    this.scrollTargetResetTimer = window.setTimeout(() => {
      this.pendingScrollLeft = null;
      this.scrollTargetResetTimer = null;
      this.updateScrollButtons();
    }, 140);

    this.updateScrollButtons();
  };

  onBottomScrollbarScroll = () => {
    const scrollPane = this.mainTablePaneRef.current;
    const bottomScrollbar = this.bottomScrollbarRef.current;

    if (!scrollPane || !bottomScrollbar) {
      return;
    }

    if (Math.abs(scrollPane.scrollLeft - bottomScrollbar.scrollLeft) > 1) {
      scrollPane.scrollLeft = bottomScrollbar.scrollLeft;
    }

    this.pendingScrollLeft = bottomScrollbar.scrollLeft;
    this.updateScrollButtons();
  };

  onTableKeyDown = event => {
    if ((event.key !== "ArrowLeft" && event.key !== "ArrowRight")
      || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    if (this.shouldIgnoreHorizontalScrollKey(event.target)) {
      return;
    }

    const direction = event.key === "ArrowLeft" ? -1 : 1;
    const canScroll = direction < 0 ? this.state.canScrollLeft : this.state.canScrollRight;
    if (!canScroll) {
      return;
    }

    event.preventDefault();
    this.scrollTable(direction, { behavior: "auto", viewportFactor: 0.9 });
  };

  shouldIgnoreHorizontalScrollKey = target => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const contentEditableParent = target.closest("[contenteditable]");
    if (target.isContentEditable || (contentEditableParent && contentEditableParent.getAttribute("contenteditable") !== "false")) {
      return true;
    }

    const formField = target.closest("input, textarea, select");
    if (!formField) {
      return false;
    }

    if (formField instanceof HTMLInputElement) {
      const textInputTypes = new Set([
        "",
        "email",
        "number",
        "password",
        "search",
        "tel",
        "text",
        "url",
      ]);

      return textInputTypes.has(formField.type);
    }

    return true;
  };

  updateScrollButtons = () => {
    const scrollPane = this.mainTablePaneRef.current;
    if (!scrollPane) {
      return;
    }

    const maxScrollLeft = scrollPane.scrollWidth - scrollPane.clientWidth;
    const canScrollLeft = scrollPane.scrollLeft > 0;
    const canScrollRight = scrollPane.scrollLeft < maxScrollLeft - 1;

    if (canScrollLeft !== this.state.canScrollLeft || canScrollRight !== this.state.canScrollRight) {
      this.setState({ canScrollLeft, canScrollRight });
    }
  };

  scheduleTableLayoutSync = () => {
    if (this.syncHeightsRaf) {
      window.cancelAnimationFrame(this.syncHeightsRaf);
    }

    this.syncHeightsRaf = window.requestAnimationFrame(() => {
      this.syncHeightsRaf = null;
      this.syncSplitTableHeights();
    });
  };

  syncSplitTableHeights = () => {
    const mainTablePane = this.mainTablePaneRef.current;
    if (!mainTablePane) {
      return;
    }

    const fixedColumnsTablePane = this.fixedColumnsTablePaneRef.current;
    const actionsTablePane = this.actionsTablePaneRef.current;
    const syncedPanes = [fixedColumnsTablePane, actionsTablePane].filter(Boolean);

    const resetHeights = pane => {
      if (!pane) {
        return;
      }
      pane.querySelectorAll("thead tr, tbody tr").forEach(row => row.style.height = "");
    };

    resetHeights(mainTablePane);
    syncedPanes.forEach(resetHeights);

    this.syncBottomScrollbar();

    if (!syncedPanes.length) {
      this.updateScrollButtons();
      return;
    }

    const syncRows = (leftRows, rightRows) => {
      const count = Math.min(leftRows.length, rightRows.length);
      for (let i = 0; i < count; i++) {
        const leftRow = leftRows[i];
        const rightRow = rightRows[i];
        const height = Math.max(leftRow.getBoundingClientRect().height, rightRow.getBoundingClientRect().height);
        const normalized = `${ Math.round(height * 1000) / 1000 }px`;
        leftRow.style.height = normalized;
        rightRow.style.height = normalized;
      }
    };

    syncedPanes.forEach(pane => {
      syncRows(mainTablePane.querySelectorAll("thead tr"), pane.querySelectorAll("thead tr"));
      syncRows(mainTablePane.querySelectorAll("tbody tr"), pane.querySelectorAll("tbody tr"));
    });
    this.updateScrollButtons();
  };

  syncBottomScrollbar = () => {
    const mainTablePane = this.mainTablePaneRef.current;
    const bottomScrollbar = this.bottomScrollbarRef.current;
    const bottomScrollbarContent = this.bottomScrollbarContentRef.current;

    if (!mainTablePane || !bottomScrollbar || !bottomScrollbarContent) {
      return;
    }

    const leftOffset = this.fixedColumnsTablePaneRef.current ? this.fixedColumnsTablePaneRef.current.offsetWidth : 0;
    const rightOffset = this.actionsTablePaneRef.current ? this.actionsTablePaneRef.current.offsetWidth : 0;

    bottomScrollbarContent.style.width = `${ mainTablePane.scrollWidth }px`;
    this.syncBottomScrollbarPosition(leftOffset, rightOffset);

    if (Math.abs(bottomScrollbar.scrollLeft - mainTablePane.scrollLeft) > 1) {
      bottomScrollbar.scrollLeft = mainTablePane.scrollLeft;
    }
  };

  syncBottomScrollbarPosition = (providedLeftOffset, providedRightOffset) => {
    const wrapper = this.tableWrapperRef.current;
    const bottomScrollbar = this.bottomScrollbarRef.current;

    if (!wrapper || !bottomScrollbar) {
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const leftOffset = _.isNumber(providedLeftOffset)
      ? providedLeftOffset
      : (this.fixedColumnsTablePaneRef.current ? this.fixedColumnsTablePaneRef.current.offsetWidth : 0);
    const rightOffset = _.isNumber(providedRightOffset)
      ? providedRightOffset
      : (this.actionsTablePaneRef.current ? this.actionsTablePaneRef.current.offsetWidth : 0);
    const scrollbarHeight = bottomScrollbar.offsetHeight || 16;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const canFloat = wrapperRect.top < viewportHeight - scrollbarHeight
      && wrapperRect.bottom > viewportHeight;

    bottomScrollbar.classList.toggle("table-bottom-scrollbar-floating", canFloat);

    if (canFloat) {
      const width = Math.max(0, wrapperRect.width - leftOffset - rightOffset);
      bottomScrollbar.style.left = `${ wrapperRect.left + leftOffset }px`;
      bottomScrollbar.style.right = "";
      bottomScrollbar.style.width = `${ width }px`;
      bottomScrollbar.style.bottom = "0px";
    } else {
      bottomScrollbar.style.left = `${ leftOffset }px`;
      bottomScrollbar.style.right = `${ rightOffset }px`;
      bottomScrollbar.style.width = "";
      bottomScrollbar.style.bottom = "0px";
    }
  };

  onRowMouseEnter = ({ currentTarget }) => {
    this.toggleHoveredRow(currentTarget.getAttribute("data-row-key"), true);
  };

  onRowMouseLeave = ({ currentTarget }) => {
    this.toggleHoveredRow(currentTarget.getAttribute("data-row-key"), false);
  };

  toggleHoveredRow = (rowKey, active) => {
    if (!rowKey) {
      return;
    }

    const toggleInPane = pane => {
      const row = this.findRowByKey(pane, rowKey);
      if (row) {
        row.classList.toggle("table-row-hover", active);
      }
    };

    toggleInPane(this.mainTablePaneRef.current);
    toggleInPane(this.fixedColumnsTablePaneRef.current);
    toggleInPane(this.actionsTablePaneRef.current);
  };

  findRowByKey = (pane, rowKey) => {
    if (!pane) {
      return null;
    }

    const rows = pane.querySelectorAll("tbody tr[data-row-key]");
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute("data-row-key") === rowKey) {
        return rows[i];
      }
    }

    return null;
  };

  exportData = eventKey => {
    const {
      data: { items },
      injects,
      list,
      renderMethods,
    } = this.props;

    const tableSchema = this.tableSchema;
    const textRenderMethods = _.assign(
      {},
      renderMethods,
      _.fromPairs(
        tableSchema.filter(schema => schema.type === "checkbox")
          .map(schema => [schema.field, value => value ? "Ja" : "Nein"]),
      ),
    );

    const fileBaseName = _.split(_.kebabCase(list), "-", 1)[0];

    const header = tableSchema.map(schema => schema.header || schema.display);
    const exportRows = _.map(items, item => {
      return _.map(tableSchema, schemaItem => {
        let value = schemaItem.type !== "markdown"
          ? formatValue(item, schemaItem, textRenderMethods, injects, this.props)
          : item[schemaItem.field];
        value = isReactElement(value) ? getInnerText(value) : value;
        if (_.isArray(value)) {
          const innerValues = value.map(v => isReactElement(v) ? getInnerText(v) : v);
          value = innerValues.join(",");
        }
        if (_.isNumber(value) && schemaItem.type !== "number") {
          value = value.toString();
        }
        if (_.isString(value) && /^[0-9]+$/.test(value)) {
          value = numeral(value).value();
        }
        if (value === false) {
          value = "";
        }
        return value;
      });
    });

    exportTable(eventKey, fileBaseName, header, exportRows);
  };

  scrollTable = (direction, options = {}) => {
    const scrollPane = this.mainTablePaneRef.current;
    if (!scrollPane) {
      return;
    }

    const {
      behavior = "smooth",
      viewportFactor = 0.8,
      minAmount = 150,
    } = options;

    const amount = Math.max(minAmount, Math.floor(scrollPane.clientWidth * viewportFactor));
    const maxScrollLeft = Math.max(0, scrollPane.scrollWidth - scrollPane.clientWidth);
    const baseLeft = this.pendingScrollLeft !== null ? this.pendingScrollLeft : scrollPane.scrollLeft;
    const targetLeft = Math.min(maxScrollLeft, Math.max(0, baseLeft + direction * amount));

    this.pendingScrollLeft = targetLeft;
    if (this.scrollTargetResetTimer) {
      window.clearTimeout(this.scrollTargetResetTimer);
      this.scrollTargetResetTimer = null;
    }

    if (scrollPane.scrollTo) {
      scrollPane.scrollTo({ left: targetLeft, behavior });
    } else {
      scrollPane.scrollLeft = targetLeft;
    }
  };
}
